import type { Rng } from "../engine/prng";
import { pick, randRange } from "../engine/prng";
import { OBSTACLE, POWERUP, SPAWN, WORLD, type ObstacleKind, type PowerUpKind } from "../constants";
import type { Obstacle, PowerUp } from "../engine/types";

let nextId = 1;

const OBSTACLE_KINDS: readonly ObstacleKind[] = ["JUMP", "DUCK", "FLY"];
const POWERUP_KINDS: readonly PowerUpKind[] = ["SHIELD", "MULTIPLIER", "LIFE"];
const POWERUP_WEIGHTS: Record<PowerUpKind, number> = {
  SHIELD: 0.5,
  MULTIPLIER: 0.35,
  LIFE: 0.15,
};

export interface SpawnerState {
  nextSpawnSec: number;
  totalSec: number;
}

export function createSpawner(): SpawnerState {
  return { nextSpawnSec: 1.2, totalSec: 0 };
}

/**
 * Advance the spawn timer. When it fires, decide what to emit and reset
 * the timer based on the seeded rng. The seeded rng makes this deterministic
 * across both multiplayer clients given an identical seed.
 */
export function tickSpawner(
  state: SpawnerState,
  rng: Rng,
  dtSec: number,
  speedPxPerSec: number,
  emit: { obstacle: (o: Obstacle) => void; powerup: (p: PowerUp) => void }
): void {
  state.totalSec += dtSec;
  state.nextSpawnSec -= dtSec;
  if (state.nextSpawnSec > 0) return;

  const isPowerup = rng() < SPAWN.POWERUP_CHANCE;
  if (isPowerup) {
    emit.powerup(makePowerUp(rng, speedPxPerSec));
  } else {
    emit.obstacle(makeObstacle(rng, speedPxPerSec));
  }

  // Spawn cadence tightens as speed grows.
  const speedFactor = Math.max(0.55, 1 - (speedPxPerSec - 360) / 1600);
  const gap =
    randRange(rng, SPAWN.MIN_GAP_SEC, SPAWN.MAX_GAP_SEC) * speedFactor;
  state.nextSpawnSec = gap;
}

function makeObstacle(rng: Rng, _speed: number): Obstacle {
  const kind = pick(rng, OBSTACLE_KINDS);
  const cfg = OBSTACLE[kind];
  let y: number;
  if (kind === "JUMP") {
    y = WORLD.GROUND_Y - cfg.height;
  } else if (kind === "DUCK") {
    y = WORLD.GROUND_Y - 110; // overhead — duck under
  } else {
    y = WORLD.GROUND_Y - 140 - rng() * 60; // flying enemy
  }
  return {
    id: nextId++,
    kind,
    x: WORLD.WIDTH + 40,
    y,
    w: cfg.width,
    h: cfg.height,
    vx: 0,
    vy: 0,
    alive: true,
  };
}

function makePowerUp(rng: Rng, _speed: number): PowerUp {
  const kind = weightedPick(rng, POWERUP_KINDS, POWERUP_WEIGHTS);
  return {
    id: nextId++,
    kind,
    x: WORLD.WIDTH + 40,
    y: WORLD.GROUND_Y - 130 - rng() * 80,
    w: POWERUP.SIZE,
    h: POWERUP.SIZE,
    vx: 0,
    vy: 0,
    alive: true,
    bobPhase: rng() * Math.PI * 2,
  };
}

function weightedPick<T extends string>(
  rng: Rng,
  items: readonly T[],
  weights: Record<T, number>
): T {
  const total = items.reduce((s, k) => s + (weights[k] ?? 1), 0);
  let r = rng() * total;
  for (const k of items) {
    r -= weights[k] ?? 1;
    if (r <= 0) return k;
  }
  return items[items.length - 1] as T;
}
